import type { Turn } from '../../types/ski-data';
import { TurnType } from '../../types/enums';
import { usePlaybackMachine } from '../../state-machines/usePlaybackMachine';
import DiagnosticTag from '../common/DiagnosticTag';

/**
 * TurnList - Scrollable list of turn cards (R2 left column, below RunOverview).
 *
 * Each 56px card shows: turn number, type tag, score, and a one-line summary.
 * Visual states:
 *   - Problem turns (score < 60): 3px left border --status-bad
 *   - Current turn: 3px left border --color-ice + --bg-card-hover
 * Clicking a card seeks playback to that turn's start_ms.
 */
interface TurnListProps {
  turns: Turn[];
}

/** Build a compact one-line summary for a turn card. */
function turnSummary(turn: Turn): string {
  const dir = turn.direction === 'Left' ? '左' : '右';
  const edge = Math.round(turn.edge_angle_avg);
  const ratio = Math.round(turn.outside_pressure_ratio * 100);
  switch (turn.turn_type) {
    case TurnType.Carving:
      return `${dir}转·卡宾 ${edge}° 外板${ratio}%`;
    case TurnType.Edging:
      return `${dir}转·走刃 ${edge}° 外板${ratio}%`;
    case TurnType.Skidding:
      return `${dir}转·搓雪 ${edge}° 外板${ratio}%`;
    default:
      return '';
  }
}

export default function TurnList({ turns }: TurnListProps) {
  const playback = usePlaybackMachine();
  const currentTurnIndex = playback.state.currentTurnIndex;

  return (
    <div className="turn-list">
      {turns.map((turn) => {
        const isCurrent = currentTurnIndex === turn.turn_index;
        const isProblem = turn.overall_score < 60;
        // State-driven border-color / background live in CSS (.turn-card and
        // its --current / --problem modifiers) so the hover rule
        // (border-color → --color-ice) can override them cleanly.
        const cardClass = isCurrent
          ? 'turn-card turn-card--current'
          : isProblem
            ? 'turn-card turn-card--problem'
            : 'turn-card';

        return (
          <div
            key={turn.turn_index}
            className={cardClass}
            onClick={() => playback.seek(turn.start_ms)}
            style={{
              height: '56px',
              flexShrink: 0,
              padding: '0 var(--space-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              cursor: 'pointer',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--fs-sm)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-secondary)',
                width: '28px',
                flexShrink: 0,
              }}
            >
              #{turn.turn_index}
            </span>
            <DiagnosticTag type={turn.turn_type} />
            <span
              style={{
                fontSize: 'var(--fs-sm)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-primary)',
                width: '30px',
                textAlign: 'right',
                flexShrink: 0,
              }}
            >
              {turn.overall_score.toFixed(0)}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {turnSummary(turn)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
